/**
 * AnimationManager - Manages animation playback for GLB scenes.
 */

import * as THREE from 'three'

export class AnimationManager
{
  animations = []
  actions = {}
  loopingAnimations = []

  constructor(animations, actions)
  {
    this.animations = animations
    this.actions = actions
    this.loopingAnimations = []
  }

  playAnimation(name, loopType = THREE.LoopOnce)
  {
    const action = this.actions[ name ]
    if (!action || action.isRunning()) return null

    action.setLoop(loopType)
    action.clampWhenFinished = true
    action.reset()
    action.play()

    const duration = action._clip.duration * 1000
    return new Promise(resolve => setTimeout(resolve, duration))
  }

  playLoopingAnimations()
  {
    this.loopingAnimations.forEach(name => this.playAnimation(name, THREE.LoopRepeat))
  }

  getLoopingAnimations()
  {
    return this.loopingAnimations
  }

  getBoundedActions(object)
  {
    const boundActions = []
    Object.entries(this.actions).forEach(([ key, action ]) =>
    {
      const binding = action?._propertyBindings?.[ 0 ]?.binding
      if (binding?.node?.name === object.name) boundActions.push(action)
    })
    return boundActions
  }

  parseAnimations(object)
  {
    const userData = object.userData

    if ('LoopingAnimations' in userData)
    {
      const loopingAnims = this.normalizeAnimationList(userData.LoopingAnimations)
      this.loopingAnimations.push(...loopingAnims)
      userData.LoopingAnimations = loopingAnims
    }

    this.parseInteractionAnimations(object)
  }

  /** @private */
  parseInteractionAnimations(object)
  {
    const userData = object.userData
    const animationKeys = [ 'OnPointerEnterAnimations', 'OnPointerExitAnimations', 'OnSelectAnimations' ]

    let hasAnimations = false
    animationKeys.forEach(key =>
    {
      if (key in userData)
      {
        userData[ key ] = this.normalizeAnimationList(userData[ key ])
        hasAnimations = true
      }
    })

    if (hasAnimations)
    {
      if (!('zone' in userData)) userData.zone = '_default_animations_zone'
      userData.type = 'interactable'

      const userDataCopy = { ...userData }
      object.traverse(node => { node.userData = userDataCopy })
    }
  }

  /** @private */
  normalizeAnimationList(value)
  {
    if (Array.isArray(value)) return value
    if (typeof value === 'string')
    {
      return value.split(',').map(s => s.trim()).filter(Boolean)
    }
    return []
  }

  cloneAnimations(object, animation)
  {
    const animationClone = animation.clone()
    animationClone.setLoop(THREE.LoopOnce)
    animationClone.clampWhenFinished = true

    if (!object.userData.animationClones) object.userData.animationClones = []
    object.userData.animationClones.push(animationClone)
  }
}
